import React, { useCallback, useEffect } from 'react';
import { Box, Link, Tooltip, Typography } from '@material-ui/core';
import {
  RemoveButton,
  ResendButton,
  Section,
  useFormEvents,
  useHasRole,
  ShowMoreButton,
  DataTable,
  EnumValueDisplay,
  Feedback,
} from '@lib/common';
import {
  CorporateAccount,
  CorporateInvoice,
  CorporateInvoiceForms,
  corporateInvoiceService,
  InvoiceType,
} from 'app/state';
import { CreateInvoice } from './create-invoice';
import { Accounting, AccountingManager, CommonForm, Admin } from '@lib/state';
import { renderInvoiceNumberCell } from './render-invoice-number-cell';

export interface Props {
  account: CorporateAccount;
  invoices: CorporateInvoice[];
  loadMore: () => void;
  isDone: boolean;
}

const DefaultNotificationMessage = 'No content available for export';

export const CorporateAccountInvoiceList: React.FC<Props> = ({
  account,
  invoices,
  loadMore,
  isDone,
}) => {
  const [{ status, error }, resetStatus] = useFormEvents(CorporateInvoiceForms.Remove);
  const [{ status: resendStatus, error: resendError }, resetResendStatus] = useFormEvents(
    CorporateInvoiceForms.Resend
  );
  const [{ status: downloadStatus, error: downloadError }, resetDownloadStatus] = useFormEvents(
    CommonForm.Export
  );

  const [showExportToast, setShowExportToast] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState(DefaultNotificationMessage);

  const accountingRole = useHasRole(Accounting, AccountingManager);
  const canDeleteInvoices = useHasRole(AccountingManager, Admin);

  const removeInvoice = useCallback(
    (invoice: CorporateInvoice) => corporateInvoiceService.removeInvoice(invoice),
    []
  );
  const resendInvoice = useCallback(
    (invoice: CorporateInvoice) => corporateInvoiceService.resendInvoice(invoice),
    []
  );

  // when the invoice list changes, reset so that the remove button can be used again
  useEffect(() => {
    resetStatus();
  }, [invoices, resetStatus]);

  useEffect(() => {
    if (downloadStatus === 'noContent') {
      setToastMessage('Tax credit memo has 0% split and will not be exported.');
      setShowExportToast(true);
      resetDownloadStatus();
    }
    if (downloadStatus === 'error') {
      setToastMessage(downloadError?.message ?? DefaultNotificationMessage);
      setShowExportToast(true);
    }
  }, [downloadStatus, downloadError, resetDownloadStatus]);

  const onToastClosed = useCallback(() => {
    setShowExportToast(false);
    resetDownloadStatus();
  }, [resetDownloadStatus]);

  const renderResendButton = useCallback(
    x => (
      <ResendButton
        onConfirm={() => resendInvoice(x)}
        onSuccess={() => resetResendStatus()}
        status={resendStatus}
        error={resendError}
        confirmView={
          <Typography>Are you sure you want to resend invoice {x.invoiceNumber}?</Typography>
        }
        toolTip="Invoice sent to AHG"
      />
    ),
    [resendInvoice, resendStatus, resendError, resetResendStatus]
  );

  const renderRemoveButton = useCallback(
    x => {
      if (account.isGroupChild && x.type === InvoiceType.TaxCreditMemo) {
        return <Typography>N/A</Typography>;
      }
      if (!canDeleteInvoices) {
        // a disabled button doesn't fire hover events, so the span carries the tooltip
        return (
          <Tooltip title="Only Accounting Managers and Admins can remove invoices." arrow>
            <span>
              <RemoveButton disabled onConfirm={() => {}} status={status} />
            </span>
          </Tooltip>
        );
      }
      return (
        <RemoveButton
          onConfirm={() => removeInvoice(x)}
          status={status}
          error={error}
          confirmView={
            <Typography>Are you sure you want to remove invoice {x.invoiceNumber}?</Typography>
          }
          confirmButtonText="Confirm Remove"
        />
      );
    },
    [removeInvoice, account, status, error, canDeleteInvoices]
  );

  const renderDownloadButton = useCallback(
    x => (
      <Link
        href="#"
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          corporateInvoiceService.downloadInvoice(x, account);
        }}
      >
        {renderInvoiceNumberCell(x.invoiceNumber, { stringLength: 11 })}
      </Link>
    ),
    [account]
  );

  return (
    <Section title="Invoices" textAlign="left">
      <Box my="1rem" display="flex" justifyContent="flex-end" flexDirection="row">
        {account && <CreateInvoice account={account} createMultipleInvoices={false} />}
      </Box>
      <Feedback
        show={showExportToast}
        onHide={onToastClosed}
        autoHide
        severity={downloadStatus === 'error' ? 'error' : 'info'}
      >
        <Typography>{toastMessage}</Typography>
      </Feedback>
      <DataTable
        items={invoices}
        children={<ShowMoreButton isDone={isDone} loadMore={loadMore} />}
        getItemKey={x => x.id}
        columns={[
          {
            title: 'Invoice No',
            valueFactory: x => renderDownloadButton(x),
          },
          {
            title: 'Type',
            valueFactory: x => <EnumValueDisplay value={x.type} />,
          },
          {
            title: 'Location',
            valueFactory: x => x.location ?? '--',
          },
          {
            title: 'Cost Center 1',
            valueFactory: x => x.costCenter1 ?? '--',
          },
          {
            title: 'Cost Center 2',
            valueFactory: x => x.costCenter2 ?? '--',
          },
          {
            title: 'Crew',
            valueFactory: x => x.crew ?? '--',
          },
          {
            title: 'Generated Date',
            valueFactory: x => x.generatedDate,
          },
          {
            title: 'Start Date',
            valueFactory: x => x.startDate,
          },
          {
            title: 'End Date',
            valueFactory: x => x.endDate,
          },
          {
            title: '',
            align: 'right',
            valueFactory: x => renderRemoveButton(x),
          },

          {
            title: ' ',
            align: 'right',
            hidden: !accountingRole,
            valueFactory: x => renderResendButton(x),
          },
        ]}
      />
    </Section>
  );
};
